/**
 * Custom hook for managing Dietician Profile Form state and logic
 *
 * Extracts form state management, validation, and event handling
 * from the DieticianProfileForm component for better separation of concerns.
 *
 * @module hooks/useDieticianProfileFormState
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { EMPTY_FEE_POLICY, type FeePolicyFields } from '@/utils/feePolicy';
import { useDieticianValidation } from '@/hooks/useDieticianValidation';
import { scrollToFirstErrorField } from '@/utils/scrollToError';
import type {
  PhoneData,
  PracticeAddressData
} from '@/types/dieticianRegistration';
import type { HealthCondition } from '@/types/referral';
import { LanguageCode } from '@/types/languageCodes';
import { getLogger } from '@/services/shared/LoggerFactory';

// Define all form fields in order for validation and scrolling
const FORM_FIELDS = [
  'firstName',
  'lastName',
  'practiceAddress.streetAddress',
  'practiceAddress.city',
  'practiceAddress.state',
  'practiceAddress.postalCode',
  'practiceAddress.country',
  'areasOfPractice',
  'languagesSpoken',
  'apdNumber',
  'phone',
  'feePolicy.initialFee',
  'feePolicy.followUpFee',
  'feePolicy.lateFee',
  'feePolicy.noticeHours',
  'feePolicy.notes',
] as const;

/**
 * Form data interface for the profile form
 */
export interface DieticianProfileFormData {
  firstName: string;
  lastName: string;
  areasOfPractice: HealthCondition[];
  languagesSpoken: LanguageCode[];
  phone: PhoneData;
  apdNumber: string;
  practiceAddress: PracticeAddressData;
  photo?: File;
  /** "Fees and payment" section (design-patient-payment-guidance §4.13.4) */
  feePolicy: FeePolicyFields;
}

/**
 * Hook configuration interface
 */
export interface UseDieticianProfileFormStateConfig {
  initialData?: Partial<DieticianProfileFormData>;
  onSubmit: (data: DieticianProfileFormData) => Promise<void>;
}

/**
 * Hook return interface
 */
export interface UseDieticianProfileFormStateReturn {
  // Form state
  formData: DieticianProfileFormData;
  fieldErrors: Record<string, string>;
  touchedFields: Set<string>;
  fieldRefs: React.RefObject<Record<string, HTMLInputElement | HTMLDivElement | null>>;

  // Form fields configuration
  FORM_FIELDS: readonly string[];

  // Utility functions
  getFieldValue: (fieldName: string) => unknown;
  validateField: (fieldName: string, value?: unknown) => boolean;

  // Event handlers
  handleFieldChange: (fieldName: string, value: unknown) => void;
  handleFieldBlur: (fieldName: string) => void;
  handlePhoneChange: (phoneData: PhoneData) => void;
  handleAreasOfPracticeChange: (areas: HealthCondition[]) => void;
  handleLanguagesChange: (languages: LanguageCode[]) => void;
  handlePhotoChange: (file: File | undefined) => void;
  handlePracticeAddressChange: (field: keyof PracticeAddressData, value: string) => void;
  handleFeePolicyChange: (field: keyof FeePolicyFields, value: string | boolean) => void;
  resetFeePolicy: () => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
}

/**
 * Custom hook for managing dietician profile form state and logic
 */
export const useDieticianProfileFormState = ({
  initialData,
  onSubmit,
}: UseDieticianProfileFormStateConfig): UseDieticianProfileFormStateReturn => {
  const logger = getLogger();
  const validation = useDieticianValidation();

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLDivElement | null>>({});

  // Form state
  const [formData, setFormData] = useState<DieticianProfileFormData>({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    areasOfPractice: initialData?.areasOfPractice || [],
    languagesSpoken: initialData?.languagesSpoken || [LanguageCode.English],
    phone: initialData?.phone || {
      countryCode: '+61',
      isoCode: 'AU',
      number: '',
    },
    apdNumber: initialData?.apdNumber || '',
    practiceAddress: initialData?.practiceAddress || {
      streetAddress: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'AU',
    },
    photo: initialData?.photo,
    feePolicy: initialData?.feePolicy ?? EMPTY_FEE_POLICY,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        phone: initialData.phone || prev.phone,
        practiceAddress: initialData.practiceAddress || prev.practiceAddress,
      }));
    }
  }, [initialData]);

  // Helper function to get field value
  const getFieldValue = useCallback((fieldName: string) => {
    if (fieldName.startsWith('practiceAddress.')) {
      const addressField = fieldName.split('.')[1] as keyof PracticeAddressData;
      return formData.practiceAddress[addressField];
    }
    if (fieldName.startsWith('feePolicy.')) {
      const feeField = fieldName.split('.')[1] as keyof FeePolicyFields;
      return formData.feePolicy[feeField];
    }
    return formData[fieldName as keyof DieticianProfileFormData];
  }, [formData]);

  // Validation functions
  const validateField = useCallback((fieldName: string, value?: unknown) => {
    const fieldValue = value ?? getFieldValue(fieldName);

    const validationMap: Record<string, () => { isValid: boolean; error?: string }> = {
      'firstName': () => validation.validateName(fieldValue as string, 'First name'),
      'lastName': () => validation.validateName(fieldValue as string, 'Last name'),
      'areasOfPractice': () => validation.validateAreasOfPractice(fieldValue as HealthCondition[]),
      'languagesSpoken': () => validation.validateLanguages(fieldValue as LanguageCode[]),
      'phone': () => validation.validatePhone(fieldValue as PhoneData),
      'apdNumber': () => validation.validateAPDNumber(fieldValue as string),
      'practiceAddress.streetAddress': () => validation.validateStreetAddress(fieldValue as string),
      'practiceAddress.city': () => validation.validateCity(fieldValue as string),
      'practiceAddress.state': () => validation.validateAddressState(fieldValue as string),
      'practiceAddress.postalCode': () => validation.validatePostalCode(fieldValue as string),
      'practiceAddress.country': () => validation.validateCountry(fieldValue as string),
      'feePolicy.initialFee': () => validation.validateMoney(fieldValue as string),
      'feePolicy.followUpFee': () => validation.validateMoney(fieldValue as string),
      'feePolicy.lateFee': () => validation.validateMoney(fieldValue as string),
      'feePolicy.noticeHours': () => validation.validateNoticeHours(fieldValue as string),
      'feePolicy.notes': () => validation.validatePaymentNotes(fieldValue as string),
    };

    const result = validationMap[fieldName]?.() || { isValid: true };

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: result.isValid ? '' : (result.error || ''),
    }));

    return result.isValid;
  }, [validation, getFieldValue]);

  // Event handlers
  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    // Validate if field has been touched
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);

  const handleFieldBlur = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));
    validateField(fieldName);
  }, [validateField]);

  // Specific handlers for complex fields
  const handlePhoneChange = useCallback((phoneData: PhoneData) => {
    handleFieldChange('phone', phoneData);
  }, [handleFieldChange]);

  const handleAreasOfPracticeChange = useCallback((areas: string[]) => {
    handleFieldChange('areasOfPractice', areas);
  }, [handleFieldChange]);

  const handleLanguagesChange = useCallback((languages: LanguageCode[]) => {
    handleFieldChange('languagesSpoken', languages);
  }, [handleFieldChange]);

  const handlePhotoChange = useCallback((file: File | undefined) => {
    handleFieldChange('photo', file);
  }, [handleFieldChange]);

  const handleFeePolicyChange = useCallback((field: keyof FeePolicyFields, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      feePolicy: {
        ...prev.feePolicy,
        [field]: value,
      },
    }));

    const fieldName = `feePolicy.${field}`;
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);

  const resetFeePolicy = useCallback(() => {
    setFormData(prev => ({ ...prev, feePolicy: { ...EMPTY_FEE_POLICY } }));
    setFieldErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith('feePolicy.')) next[key] = '';
      });
      return next;
    });
  }, []);

  const handlePracticeAddressChange = useCallback((field: keyof PracticeAddressData, value: string) => {
    setFormData(prev => ({
      ...prev,
      practiceAddress: {
        ...prev.practiceAddress,
        [field]: value,
      },
    }));

    // Validate specific practice address field if it has been touched
    const fieldName = `practiceAddress.${field}`;
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);

  // Form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Mark all fields as touched
    const newTouchedFields = new Set(FORM_FIELDS as readonly string[]);
    setTouchedFields(newTouchedFields);

    // Validate all fields and collect results
    const validationResults: { field: string; isValid: boolean; error?: string }[] = [];
    const newFieldErrors: Record<string, string> = {};

    FORM_FIELDS.forEach((field: string) => {
      const fieldValue = getFieldValue(field);
      const validationMap: Record<string, () => { isValid: boolean; error?: string }> = {
        'firstName': () => validation.validateName(fieldValue as string, 'First name'),
        'lastName': () => validation.validateName(fieldValue as string, 'Last name'),
        'areasOfPractice': () => validation.validateAreasOfPractice(fieldValue as HealthCondition[]),
        'languagesSpoken': () => validation.validateLanguages(fieldValue as LanguageCode[]),
        'phone': () => validation.validatePhone(fieldValue as PhoneData),
        'apdNumber': () => validation.validateAPDNumber(fieldValue as string),
        'practiceAddress.streetAddress': () => validation.validateStreetAddress(fieldValue as string),
        'practiceAddress.city': () => validation.validateCity(fieldValue as string),
        'practiceAddress.state': () => validation.validateAddressState(fieldValue as string),
        'practiceAddress.postalCode': () => validation.validatePostalCode(fieldValue as string),
        'practiceAddress.country': () => validation.validateCountry(fieldValue as string),
        'feePolicy.initialFee': () => validation.validateMoney(fieldValue as string),
        'feePolicy.followUpFee': () => validation.validateMoney(fieldValue as string),
        'feePolicy.lateFee': () => validation.validateMoney(fieldValue as string),
        'feePolicy.noticeHours': () => validation.validateNoticeHours(fieldValue as string),
        'feePolicy.notes': () => validation.validatePaymentNotes(fieldValue as string),
      };

      const result = validationMap[field]?.() || { isValid: true };
      validationResults.push({ field, ...result });
      newFieldErrors[field] = result.isValid ? '' : (result.error || '');
    });

    // Update field errors state
    setFieldErrors(newFieldErrors);

    const isFormValid = validationResults.every(result => result.isValid);

    if (!isFormValid) {
      // Create immediate validation state for scroll-to-error
      const immediateValidationState: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }> = {};

      FORM_FIELDS.forEach((field: string) => {
        const validationResult = validationResults.find(r => r.field === field);
        immediateValidationState[field] = {
          touched: true,
          result: {
            isValid: validationResult?.isValid ?? true,
            error: validationResult?.error
          }
        };
      });

      // Use immediate validation state for scrolling
      setTimeout(() => {
        scrollToFirstErrorField([...FORM_FIELDS], immediateValidationState, fieldRefs.current);
      }, 100);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is managed by parent component
      logger.error('Profile form submission error:', error);
    }
  }, [validation, getFieldValue, onSubmit, formData]);

  return {
    // Form state
    formData,
    fieldErrors,
    touchedFields,
    fieldRefs,

    // Form fields configuration
    FORM_FIELDS,

    // Utility functions
    getFieldValue,
    validateField,

    // Event handlers
    handleFieldChange,
    handleFieldBlur,
    handlePhoneChange,
    handleAreasOfPracticeChange,
    handleLanguagesChange,
    handlePhotoChange,
    handlePracticeAddressChange,
    handleFeePolicyChange,
    resetFeePolicy,
    handleSubmit,
  };
};